import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { THEMES, Theme, ThemeName, DEFAULT_THEME } from '@/constants/themes';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  theme: Theme;
  themeName: ThemeName;
  setTheme: (themeName: ThemeName) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [themeName, setThemeName] = useState<ThemeName>(DEFAULT_THEME);
  const [theme, setThemeState] = useState<Theme>(THEMES[DEFAULT_THEME]);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (profile?.theme) {
      const savedTheme = profile.theme as ThemeName;
      if (THEMES[savedTheme]) {
        setThemeName(savedTheme);
        setThemeState(THEMES[savedTheme]);
      }
    }
  }, [profile]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const setTheme = async (newThemeName: ThemeName) => {
    if (!THEMES[newThemeName]) return;

    setThemeName(newThemeName);
    setThemeState(THEMES[newThemeName]);

    // Debounce the Supabase profile update by 1 second
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (profile?.id) {
      saveTimerRef.current = setTimeout(() => {
        supabase
          .from('profiles')
          .update({ theme: newThemeName })
          .eq('id', profile.id)
          .then();
      }, 1000);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, themeName, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
